import React from 'react';
import { motion } from 'framer-motion';
import { FiInbox, FiSearch, FiAlertCircle, FiFileText, FiDatabase } from 'react-icons/fi';
import { cn } from '../../lib/utils';
import { Button } from './Button';

const EmptyState = ({
  variant = 'no-data',
  title,
  description,
  icon,
  actionLabel,
  onAction,
  children,
  className,
  illustration,
}) => {
  const variantConfig = {
    'no-data': {
      icon: <FiInbox className="h-12 w-12" />,
      iconBg: 'bg-neutral-100',
      iconColor: 'text-neutral-400',
      defaultTitle: 'No Data Available',
      defaultDescription: 'There are no items to display at the moment.',
    },
    'search': {
      icon: <FiSearch className="h-12 w-12" />,
      iconBg: 'bg-primary-100',
      iconColor: 'text-primary-600',
      defaultTitle: 'No Results Found',
      defaultDescription: 'We couldn\'t find anything matching your search. Try different keywords.',
    },
    'error': {
      icon: <FiAlertCircle className="h-12 w-12" />,
      iconBg: 'bg-danger-100',
      iconColor: 'text-danger-600',
      defaultTitle: 'Something Went Wrong',
      defaultDescription: 'We encountered an error loading this data. Please try again.',
    },
    'no-results': {
      icon: <FiFileText className="h-12 w-12" />,
      iconBg: 'bg-warning-100',
      iconColor: 'text-warning-600',
      defaultTitle: 'No Results',
      defaultDescription: 'No records match your current filters.',
    },
    'custom': {
      icon: <FiDatabase className="h-12 w-12" />,
      iconBg: 'bg-neutral-100',
      iconColor: 'text-neutral-400',
      defaultTitle: 'Empty',
      defaultDescription: 'No content available.',
    },
  };

  const config = variantConfig[variant] || variantConfig['no-data'];
  const displayTitle = title || config.defaultTitle;
  const displayDescription = description || config.defaultDescription;
  const displayIcon = icon || config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className={cn('flex flex-col items-center justify-center text-center py-12 px-4', className)}
    >
      {illustration ? (
        <div className="mb-6">{illustration}</div>
      ) : (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.1 }}
          className={cn('h-24 w-24 rounded-full flex items-center justify-center mb-6', config.iconBg)}
        >
          <div className={config.iconColor}>{displayIcon}</div>
        </motion.div>
      )}

      <motion.h3 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-xl font-bold text-neutral-900 mb-2">
        {displayTitle}
      </motion.h3>

      <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="text-sm text-neutral-600 max-w-md mb-6">
        {displayDescription}
      </motion.p>

      {children && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="w-full max-w-md">
          {children}
        </motion.div>
      )}

      {actionLabel && onAction && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
          <Button onClick={onAction} variant="primary">{actionLabel}</Button>
        </motion.div>
      )}
    </motion.div>
  );
};

export const SearchNoResults = ({ searchTerm, onClearSearch }) => (
  <EmptyState variant="search" title={`No results for "${searchTerm}"`} description="Try adjusting your search terms or filters." actionLabel="Clear Search" onAction={onClearSearch} />
);

export const ErrorState = ({ onRetry, message }) => (
  <EmptyState variant="error" title="Oops! Something Went Wrong" description={message || "We encountered an unexpected error. Please try again."} actionLabel="Try Again" onAction={onRetry} />
);

export default EmptyState;
